import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import {
  AutoAwesome as AutoAwesomeIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { FaCapsules } from "react-icons/fa";
import useAuth from "../../../hooks/useAuth";
import { useAlert } from "../../../context/AlertProvider";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import {
  ActionButton,
  FormAutocomplete,
  FormSelect,
  FormTextField,
} from "../../../components/atoms";
import { DrawerField, tileInputProps } from "../../../components/molecules";
import TemplateListPage, { statusParam } from "../../../components/organisms/TemplateListPage";
import TemplateEditorDrawer, { drawerGridSx } from "../../../components/organisms/TemplateEditorDrawer";
import { StatusPill } from "../../../components/organisms/ClinicRecordList";
import { fullDateTime, relativePast } from "../../Secretary/PatientsPage/patientListFormat";
import {
  useCreateDoctorMedicineTemplateMutation,
  useListDoctorMedicineTemplatesMutation,
  useSetDoctorMedicineTemplateStatusMutation,
  useUpdateDoctorMedicineTemplateMutation,
} from "../../../store/doctorApi";
import { useListMedicineTemplatesMutation } from "../../../store/systemVariablesApi";

const PERIOD_UNITS = ["Gün", "Hafta", "Ay"];

const EMPTY_MEDICINE_FORM = {
  medicine: null,
  dose1: "",
  dose2: "",
  usagePeriodValue: "",
  usagePeriodUnit: "Gün",
  box: "",
  quantity: "",
  usageMethod: "",
};

// Şablondaki bir ilacın kısa kullanım özeti (drawer'daki öğe satırı).
const medicineSummary = (m) =>
  [
    `Doz 1: ${m.dose1 ?? "-"}`,
    `Doz 2: ${m.dose2 ?? "-"}`,
    m.usagePeriod?.value ? `${m.usagePeriod.value} ${m.usagePeriod.unit || ""}`.trim() : null,
    m.box ? `${m.box} kutu` : null,
    m.quantity ? `${m.quantity} adet` : null,
    m.usageMethod || null,
  ]
    .filter(Boolean)
    .join(" · ");

export default function DoctorMedicineTemplates() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", items: [] });
  const [medicineForm, setMedicineForm] = useState(EMPTY_MEDICINE_FORM);

  const [medicineTemplates, setMedicineTemplates] = useState([]);

  const bootRef = useRef(false);
  const [listDoctorMedicineTemplates] = useListDoctorMedicineTemplatesMutation();
  const [createDoctorMedicineTemplate, { isLoading: isCreateLoading }] =
    useCreateDoctorMedicineTemplateMutation();
  const [updateDoctorMedicineTemplate, { isLoading: isUpdateLoading }] =
    useUpdateDoctorMedicineTemplateMutation();
  const [setDoctorMedicineTemplateStatus] =
    useSetDoctorMedicineTemplateStatusMutation();
  const [listMedicineTemplates] = useListMedicineTemplatesMutation();

  const isSaving = isCreateLoading || isUpdateLoading;

  const fetchList = async () => {
    if (!token) return;
    setErrorMsg("");
    setIsLoading(true);
    try {
      const res = await listDoctorMedicineTemplates({
        token,
        query,
        status: statusParam(status),
        page: page + 1,
        limit: rowsPerPage,
      }).unwrap();
      setItems(res?.data?.templates || []);
      setTotal(res?.data?.pagination?.total || 0);
    } catch (e) {
      setErrorMsg(e?.data?.message || "Şablonlar alınamadı.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMedicineTemplates = async () => {
    if (!token) return;
    try {
      const res = await listMedicineTemplates({
        token,
        status: "active",
        limit: 500,
      }).unwrap();
      setMedicineTemplates(res?.data?.items || []);
    } catch (e) {
      setMedicineTemplates([]);
    }
  };

  useEffect(() => {
    if (!token || bootRef.current) return;
    bootRef.current = true;
    fetchList();
    fetchMedicineTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, status, page, rowsPerPage]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", items: [] });
    setMedicineForm(EMPTY_MEDICINE_FORM);
    setOpen(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    setForm({ name: template.name || "", items: template.items || [] });
    setMedicineForm(EMPTY_MEDICINE_FORM);
    setOpen(true);
  };

  const onAddItem = () => {
    if (!medicineForm.medicine) return;
    const exists = (form.items || []).some(
      (m) => String(m.medicine) === String(medicineForm.medicine._id)
    );
    if (exists) return;
    setForm((p) => ({
      ...p,
      items: [
        ...(p.items || []),
        {
          medicine: medicineForm.medicine._id,
          name: medicineForm.medicine.name,
          dose1: medicineForm.dose1 ? Number(medicineForm.dose1) : undefined,
          dose2: medicineForm.dose2 ? Number(medicineForm.dose2) : undefined,
          usagePeriod:
            medicineForm.usagePeriodValue && medicineForm.usagePeriodUnit
              ? {
                  value: Number(medicineForm.usagePeriodValue),
                  unit: medicineForm.usagePeriodUnit,
                }
              : undefined,
          box: medicineForm.box ? Number(medicineForm.box) : undefined,
          quantity: medicineForm.quantity ? Number(medicineForm.quantity) : undefined,
          usageMethod: medicineForm.usageMethod || "",
        },
      ],
    }));
    setMedicineForm(EMPTY_MEDICINE_FORM);
  };

  const onRemoveItem = (index) => {
    setForm((p) => ({
      ...p,
      items: (p.items || []).filter((_, i) => i !== index),
    }));
  };

  const autoGenerate = () => {
    if (medicineTemplates.length === 0) return;
    const pick = (arr, n) => {
      const shuffled = [...arr].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, Math.min(n, shuffled.length));
    };
    const units = ["Gün", "Hafta", "Ay"];
    const methods = ["Ağızdan", "Göze damlatma", "Topikal", "Kas içi enjeksiyon"];
    const count = Math.floor(Math.random() * 4) + 2;
    const selected = pick(medicineTemplates, count);
    const generatedItems = selected.map((m) => ({
      medicine: m._id,
      name: m.name,
      dose1: Math.floor(Math.random() * 3) + 1,
      dose2: Math.floor(Math.random() * 3) + 1,
      usagePeriod: {
        value: Math.floor(Math.random() * 14) + 1,
        unit: units[Math.floor(Math.random() * units.length)],
      },
      box: Math.floor(Math.random() * 3) + 1,
      quantity: Math.floor(Math.random() * 5) + 1,
      usageMethod: methods[Math.floor(Math.random() * methods.length)],
    }));
    setForm({
      name: `Test İlaç Şablonu ${Math.floor(Math.random() * 900) + 100}`,
      items: generatedItems,
    });
  };

  const onSave = async () => {
    if (!token || !form.name.trim()) return;
    try {
      if (editing) {
        await updateDoctorMedicineTemplate({
          token,
          templateId: editing._id,
          name: form.name,
          items: form.items,
        }).unwrap();
        showSuccess("Şablon başarıyla güncellendi");
      } else {
        await createDoctorMedicineTemplate({
          token,
          name: form.name,
          items: form.items,
        }).unwrap();
        showSuccess("Şablon başarıyla oluşturuldu");
      }
      setOpen(false);
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Kaydetme işlemi başarısız.");
    }
  };

  const [togglingStatus, setTogglingStatus] = useState(false);
  const onToggleStatus = async (template) => {
    if (!token) return;
    setTogglingStatus(true);
    try {
      await setDoctorMedicineTemplateStatus({
        token,
        templateId: template._id,
        isActive: !template.isActive,
      }).unwrap();
      showSuccess(template.isActive ? "Şablon pasife alındı" : "Şablon aktifleştirildi");
      setEditing((prev) => (prev && prev._id === template._id ? { ...prev, isActive: !template.isActive } : prev));
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Durum güncellenemedi.");
    } finally {
      setTogglingStatus(false);
    }
  };

  const canSave = useMemo(() => form.name.trim().length > 2, [form.name]);

  const subText = { fontSize: TYPO.caption, color: COLORS.slate500 };
  const itemNames = (row) => (row.items || []).map((i) => i?.name).filter(Boolean);
  const setMed = (key) => (e) => setMedicineForm((p) => ({ ...p, [key]: e.target.value }));
  const fieldProps = {
    palette,
    variant: "standard",
    size: "small",
    fullWidth: true,
    InputProps: tileInputProps(true),
  };
  const formItems = form.items || [];

  return (
    <TemplateListPage
      icon={<FaCapsules size={20} />}
      title="İlaç Şablonları"
      subtitle="Tedaviye hızlı ilaç eklemek için şablonlar"
      query={query}
      onQueryChange={(v) => {
        setPage(0);
        setQuery(v);
      }}
      status={status}
      onStatusChange={(v) => {
        setPage(0);
        setStatus(v);
      }}
      onCreate={openCreate}
      errorMsg={errorMsg}
      isLoading={isLoading}
      columns={[
        { key: "name", label: "Şablon", width: "32%" },
        { key: "items", label: "İlaçlar", width: "40%" },
        { key: "updated", label: "Güncellendi", width: "14%" },
        { key: "status", label: "Durum", width: "14%" },
      ]}
      rows={items}
      onRowClick={openEdit}
      renderRowCells={(row) => {
        const names = itemNames(row);
        const updated = row.updatedAt || row.createdAt;
        return [
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }} noWrap>
            {row.name}
          </Typography>,
          names.length ? (
            <Box sx={{ minWidth: 0, pr: 2 }}>
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} noWrap>
                {names.slice(0, 3).join(", ")}
              </Typography>
              <Typography sx={subText}>
                {names.length} ilaç{names.length > 3 ? ` · +${names.length - 3} daha` : ""}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>
          ),
          updated ? (
            <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} title={fullDateTime(updated)}>
              {relativePast(updated)}
            </Typography>
          ) : (
            <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>
          ),
          <StatusPill label={row.isActive ? "Aktif" : "Pasif"} tone={row.isActive ? "success" : "neutral"} />,
        ];
      }}
      emptyIcon={<FaCapsules size={22} />}
      emptyMessage="Sık yazdığınız ilaç gruplarını dozlarıyla birlikte şablon olarak kaydedin."
      page={page}
      rowsPerPage={rowsPerPage}
      total={total}
      onPageChange={(_, newPage) => setPage(newPage)}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
    >
      <TemplateEditorDrawer
        open={open}
        onClose={() => setOpen(false)}
        icon={<FaCapsules size={18} />}
        title={editing ? "Şablonu düzenle" : "Yeni ilaç şablonu"}
        subtitle={editing ? editing.name : "Tedaviye hızlıca ilaç eklemek için şablon oluşturun"}
        onSubmit={onSave}
        submitLabel={editing ? "Güncelle" : "Kaydet"}
        submitDisabled={!canSave}
        loading={isSaving}
        width={600}
        statusControl={
          editing
            ? { isActive: Boolean(editing.isActive), onToggle: () => onToggleStatus(editing), loading: togglingStatus }
            : null
        }
        headerExtra={
          !editing ? (
            <Button
              startIcon={<AutoAwesomeIcon />}
              variant="outlined"
              size="small"
              onClick={autoGenerate}
              disabled={medicineTemplates.length === 0}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: COLORS.warning,
                color: COLORS.warningDark,
                "&:hover": { bgcolor: COLORS.warningLightest, borderColor: COLORS.warningDark },
              }}
            >
              Otomatik doldur (test)
            </Button>
          ) : null
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <DrawerField label="Şablon adı *" hint="En az 3 karakter.">
            <FormTextField
              {...fieldProps}
              placeholder="Ör. Üst solunum yolu enfeksiyonu"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </DrawerField>

          {/* İlaç ekleme */}
          <Box sx={{ pt: 2.5, borderTop: `1px solid ${COLORS.slate100}` }}>
            <Typography
              sx={{
                fontSize: TYPO.caption,
                fontWeight: 600,
                color: COLORS.slate500,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 2,
              }}
            >
              İlaç ekle
            </Typography>
            <Box sx={drawerGridSx}>
              <DrawerField label="İlaç" fullWidth>
                <FormAutocomplete
                  palette={palette}
                  variant="standard"
                  placeholder="İlaç ara"
                  options={medicineTemplates}
                  value={medicineForm.medicine}
                  onChange={(_, value) => setMedicineForm((p) => ({ ...p, medicine: value }))}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  noOptionsText="İlaç bulunamadı"
                  textFieldProps={{
                    sx: {
                      "& .MuiInputBase-input": {
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: COLORS.slate800,
                      },
                    },
                  }}
                />
              </DrawerField>
              <DrawerField label="Doz 1">
                <FormTextField {...fieldProps} type="number" value={medicineForm.dose1} onChange={setMed("dose1")} />
              </DrawerField>
              <DrawerField label="Doz 2">
                <FormTextField {...fieldProps} type="number" value={medicineForm.dose2} onChange={setMed("dose2")} />
              </DrawerField>
              <DrawerField label="Kullanım süresi">
                <FormTextField
                  {...fieldProps}
                  type="number"
                  value={medicineForm.usagePeriodValue}
                  onChange={setMed("usagePeriodValue")}
                />
              </DrawerField>
              <DrawerField label="Süre birimi">
                <FormSelect
                  palette={palette}
                  variant="standard"
                  size="small"
                  fullWidth
                  InputProps={tileInputProps(true)}
                  value={medicineForm.usagePeriodUnit}
                  onChange={(value) => setMedicineForm((p) => ({ ...p, usagePeriodUnit: value }))}
                  options={PERIOD_UNITS}
                  placeholder={null}
                />
              </DrawerField>
              <DrawerField label="Kutu">
                <FormTextField {...fieldProps} type="number" value={medicineForm.box} onChange={setMed("box")} />
              </DrawerField>
              <DrawerField label="Adet">
                <FormTextField {...fieldProps} type="number" value={medicineForm.quantity} onChange={setMed("quantity")} />
              </DrawerField>
              <DrawerField label="Kullanım şekli" fullWidth>
                <FormTextField
                  {...fieldProps}
                  placeholder="Ör. Ağızdan, tok karnına"
                  value={medicineForm.usageMethod}
                  onChange={setMed("usageMethod")}
                />
              </DrawerField>
            </Box>
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <ActionButton
                variant="add"
                palette={palette}
                label="İlacı ekle"
                onClick={onAddItem}
                disabled={!medicineForm.medicine}
              />
            </Box>
          </Box>

          {/* Şablondaki ilaçlar */}
          <Box sx={{ pt: 2.5, borderTop: `1px solid ${COLORS.slate100}` }}>
            <Typography
              sx={{
                fontSize: TYPO.caption,
                fontWeight: 600,
                color: COLORS.slate500,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 1,
              }}
            >
              Şablon ilaçları{formItems.length ? ` · ${formItems.length}` : ""}
            </Typography>
            {formItems.length ? (
              formItems.map((m, idx) => (
                <Box
                  key={`${m.medicine}-${idx}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1.25,
                    borderBottom: idx < formItems.length - 1 ? `1px solid ${COLORS.slate100}` : "none",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }} noWrap>
                      {m.name || "İlaç"}
                    </Typography>
                    <Typography sx={subText}>{medicineSummary(m)}</Typography>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label="İlacı kaldır"
                    onClick={() => onRemoveItem(idx)}
                    sx={{ color: COLORS.slate400, "&:hover": { color: COLORS.errorDark } }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            ) : (
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate400, py: 1 }}>
                Henüz ilaç eklenmedi.
              </Typography>
            )}
          </Box>
        </Box>
      </TemplateEditorDrawer>
    </TemplateListPage>
  );
}
